import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Child } from '../types';
import TimePickerDropdown from './TimePickerDropdown';
import './SOCSSetupModal.css'; // Reuse SOCS modal styles

interface SchoolsBuddySetupModalProps {
  isOpen: boolean;
  onClose: (
    completed: boolean,
    termPlannerData?: { termPlannerUuid: string; childUuid: string },
    needsTermPlannerCreation?: { childUuid: string; childName: string }[]
  ) => void;
  children: Child[];
  suggestedEmails?: string[];
  defaultEmail?: string;
  onEmailUsed?: (email: string) => void;
}

interface MatchedChild {
  schoolsBuddyName: string;
  schoolsBuddyChildId: string;
  cluubzChildName: string;
  cluubzChildUuid: string;
  confidenceScore: number;
}

const SchoolsBuddySetupModal: React.FC<SchoolsBuddySetupModalProps> = ({ isOpen, onClose, children, suggestedEmails = [], defaultEmail = '', onEmailUsed }) => {
  const { user } = useAuth();
  const apiService = ApiService.getInstance();

  // Steps: 1 = School Search, 2 = Credentials, 3 = Connecting, 4 = Connection Success (review), 5 = Term Config per child, 6 = Importing
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: School search per child
  const [childSchoolNames, setChildSchoolNames] = useState<{ [childUuid: string]: string }>({});
  const [searchResults, setSearchResults] = useState<{ [childUuid: string]: any[] }>({});
  const [searching, setSearching] = useState<{ [childUuid: string]: boolean }>({});
  const [selectedSite, setSelectedSite] = useState<{ [childUuid: string]: string }>({});
  const [showSearchExpanded, setShowSearchExpanded] = useState<{ [childUuid: string]: boolean }>({});
  const [manualUrls, setManualUrls] = useState<{ [childUuid: string]: string }>({});
  const [autoSearchTriggered, setAutoSearchTriggered] = useState<{ [childUuid: string]: boolean }>({});

  // Step 2: Credentials - per child
  const [childCredentials, setChildCredentials] = useState<{ [childUuid: string]: { email: string; password: string } }>({});
  const [showPassword, setShowPassword] = useState<{ [childUuid: string]: boolean }>({});
  const [useSameCredentials, setUseSameCredentials] = useState(false);
  const [sharedEmail, setSharedEmail] = useState('');
  const [sharedPassword, setSharedPassword] = useState('');
  const [showSharedPassword, setShowSharedPassword] = useState(false);
  const [selectedSchoolUrl, setSelectedSchoolUrl] = useState('');
  const [showCustomEmailInput, setShowCustomEmailInput] = useState(false);
  const [showCustomEmailInputPerChild, setShowCustomEmailInputPerChild] = useState<{ [childUuid: string]: boolean }>({});

  // Step 4: Term configuration - matched children from link response
  const [matchedChildren, setMatchedChildren] = useState<MatchedChild[]>([]);
  const [currentChildIndex, setCurrentChildIndex] = useState(0);
  const [termName, setTermName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [schoolStartTime, setSchoolStartTime] = useState('08:45');
  const [schoolEndTime, setSchoolEndTime] = useState('15:30');

  // Track previous isOpen state to detect fresh open vs resume
  const wasOpen = React.useRef(false);

  // Reset state when modal opens (only on FRESH open, not resume from background)
  useEffect(() => {
    if (isOpen) {
      // Only reset state on FRESH open (was closed, now open)
      // Don't reset on resume from background (was open, still open)
      if (!wasOpen.current) {
        setStep(1);
        setError(null);
        setChildCredentials({});
        setShowPassword({});
        setUseSameCredentials(false);
        setSharedEmail(defaultEmail || ''); // Pre-populate with default email
        setSharedPassword('');
        setShowSharedPassword(false);
        setSelectedSchoolUrl('');
        setShowCustomEmailInput(false);
        setShowCustomEmailInputPerChild({});
        setMatchedChildren([]);
        setCurrentChildIndex(0);
        setTermName('');
        setStartDate('');
        setEndDate('');
        setSchoolStartTime('08:45');
        setSchoolEndTime('15:30');

        // Initialize child school names from their school data
        const schoolNames: { [key: string]: string } = {};
        const autoSearchFlags: { [key: string]: boolean } = {};

        children.forEach(child => {
          const schoolName = (child as any).school_venue || (child as any).school || '';
          schoolNames[child.uuid] = schoolName;
          // Set to false so useEffect will trigger auto-search if school name exists
          autoSearchFlags[child.uuid] = false;
        });
        setChildSchoolNames(schoolNames);
        setAutoSearchTriggered(autoSearchFlags);
        setSearchResults({});
        setSearching({});
        setSelectedSite({});
        setShowSearchExpanded({});
        setManualUrls({});
        // Auto-search is now handled by useEffect that watches for state changes
      }
      wasOpen.current = true;
    } else {
      wasOpen.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, children]);

  // Auto-trigger search when modal opens with pre-populated school data
  // This useEffect watches for state changes and triggers search when ready
  useEffect(() => {
    if (isOpen && step === 1) {
      children.forEach((child) => {
        const schoolName = childSchoolNames[child.uuid]?.trim();
        // Only auto-search if:
        // 1. There's a school name from child's profile
        // 2. We haven't already triggered auto-search for this child
        // 3. There are no existing search results
        // 4. Not currently searching
        // 5. No site already selected
        if (
          schoolName &&
          autoSearchTriggered[child.uuid] === false &&
          (!searchResults[child.uuid] || searchResults[child.uuid].length === 0) &&
          !searching[child.uuid] &&
          !selectedSite[child.uuid]
        ) {
          setAutoSearchTriggered(prev => ({ ...prev, [child.uuid]: true }));
          handleSearchSchool(child.uuid);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, children, childSchoolNames, autoSearchTriggered]);

  // Set default term dates when entering step 5 (term config)
  useEffect(() => {
    if (step === 5 && !termName) {
      const now = new Date();
      const currentMonth = now.getMonth();
      const day = now.getDate();

      // Determine term based on month (UK academic year with half-term splits)
      // Autumn Half 1: Sept - Oct, Autumn Half 2: Nov - Dec
      // Spring Half 1: Jan - Feb, Spring Half 2: Mar - Apr
      // Summer Half 1: Apr-May, Summer Half 2: Jun - Jul
      if (currentMonth >= 8 && currentMonth <= 9) {
        // Sept-Oct: Autumn Half 1
        setTermName('Autumn Half 1');
        setStartDate(`${now.getFullYear()}-09-01`);
        setEndDate(`${now.getFullYear()}-10-25`);
      } else if (currentMonth >= 10 && currentMonth <= 11) {
        // Nov-Dec: Autumn Half 2
        setTermName('Autumn Half 2');
        setStartDate(`${now.getFullYear()}-11-01`);
        setEndDate(`${now.getFullYear()}-12-20`);
      } else if (currentMonth >= 0 && currentMonth <= 1) {
        // Jan-Feb: Spring Half 1
        setTermName('Spring Half 1');
        setStartDate(`${now.getFullYear()}-01-06`);
        setEndDate(`${now.getFullYear()}-02-14`);
      } else if (currentMonth >= 2 && currentMonth <= 3) {
        // Mar-Apr: Spring Half 2
        setTermName('Spring Half 2');
        setStartDate(`${now.getFullYear()}-02-24`);
        setEndDate(`${now.getFullYear()}-04-10`);
      } else if (currentMonth >= 4 && currentMonth <= 5) {
        // Apr-May: Summer Half 1
        setTermName('Summer Half 1');
        setStartDate(`${now.getFullYear()}-04-20`);
        setEndDate(`${now.getFullYear()}-05-23`);
      } else {
        // Jun-Jul: Summer Half 2
        setTermName('Summer Half 2');
        setStartDate(`${now.getFullYear()}-06-02`);
        setEndDate(`${now.getFullYear()}-07-20`);
      }
    }
  }, [step, termName]);

  const handleSearchSchool = async (childUuid: string) => {
    const schoolName = childSchoolNames[childUuid];
    if (!schoolName?.trim()) return;

    setAutoSearchTriggered(prev => ({ ...prev, [childUuid]: true }));
    setSearching(prev => ({ ...prev, [childUuid]: true }));
    setError(null);

    try {
      const response = await apiService.searchSchoolsBuddySchool(schoolName.trim());

      if (response.success && response.data?.sites && response.data.sites.length > 0) {
        setSearchResults(prev => ({ ...prev, [childUuid]: response.data.sites }));
        setSelectedSite(prev => ({ ...prev, [childUuid]: response.data.sites[0].url }));
        setShowSearchExpanded(prev => ({ ...prev, [childUuid]: false }));
      } else {
        setSearchResults(prev => ({ ...prev, [childUuid]: [] }));
        setShowSearchExpanded(prev => ({ ...prev, [childUuid]: true }));
      }
    } catch (err: any) {
      console.error('School search error:', err);
      setSearchResults(prev => ({ ...prev, [childUuid]: [] }));
      setShowSearchExpanded(prev => ({ ...prev, [childUuid]: true }));
    } finally {
      setSearching(prev => ({ ...prev, [childUuid]: false }));
    }
  };

  const handleSelectSite = (childUuid: string, url: string) => {
    setSelectedSite(prev => ({ ...prev, [childUuid]: url }));
    setShowSearchExpanded(prev => ({ ...prev, [childUuid]: false }));
  };

  const getMatchedChildrenCount = () => {
    return children.filter(child => selectedSite[child.uuid] || manualUrls[child.uuid]).length;
  };

  // Get children that have a school selected (excluding parent-child records)
  const getChildrenWithSchools = () => {
    return children.filter(child => !child.is_parent && (selectedSite[child.uuid] || manualUrls[child.uuid]));
  };

  // Get the school URL for a child
  const getSchoolUrlForChild = (childUuid: string) => {
    return selectedSite[childUuid] || manualUrls[childUuid] || '';
  };

  // Get the school name from URL for display
  const getSchoolNameFromUrl = (url: string) => {
    const match = url.match(/https?:\/\/([^.]+)\.schoolsbuddy/);
    return match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : url;
  };

  // Check if all credentials are filled
  const areCredentialsComplete = () => {
    const childrenWithSchools = getChildrenWithSchools();
    if (childrenWithSchools.length === 0) return false;

    if (useSameCredentials) {
      return !!(sharedEmail && sharedPassword);
    } else {
      return childrenWithSchools.every(child => {
        const creds = childCredentials[child.uuid];
        return creds && creds.email && creds.password;
      });
    }
  };

  const getFirstMatchedSchoolUrl = () => {
    for (const child of children) {
      if (selectedSite[child.uuid]) return selectedSite[child.uuid];
      if (manualUrls[child.uuid]) return manualUrls[child.uuid];
    }
    return '';
  };

  const handleContinueToCredentials = () => {
    const schoolUrl = getFirstMatchedSchoolUrl();
    if (!schoolUrl) {
      setError('Please search for and select at least one school');
      return;
    }
    setSelectedSchoolUrl(schoolUrl);
    setStep(2);
  };

  const handleConnect = async () => {
    if (!areCredentialsComplete()) {
      setError('Please fill in all credential fields');
      return;
    }

    setLoading(true);
    setError(null);
    setStep(3);

    try {
      const childrenWithSchools = getChildrenWithSchools();
      let allMatched: MatchedChild[] = [];
      let connectionErrors: string[] = [];

      // For each child with a school, connect using their credentials
      for (const child of childrenWithSchools) {
        const schoolUrl = getSchoolUrlForChild(child.uuid);
        const email = useSameCredentials ? sharedEmail : (childCredentials[child.uuid]?.email || '');
        const password = useSameCredentials ? sharedPassword : (childCredentials[child.uuid]?.password || '');
        const schoolName = childSchoolNames[child.uuid] || 'School';

        try {
          console.log(`Connecting ${child.first_name} to ${schoolUrl}...`);
          const response = await apiService.linkSchoolsBuddyAccount(email, password, schoolName, schoolUrl);

          if (response.success && response.data) {
            console.log(`SchoolsBuddy linked successfully for ${child.first_name}`);
            const matched = response.data.matched || [];
            allMatched = [...allMatched, ...matched];
          } else {
            connectionErrors.push(`${child.first_name}: ${response.error || 'Failed to connect'}`);
          }
        } catch (err: any) {
          connectionErrors.push(`${child.first_name}: ${err.message || 'Failed to connect'}`);
        }
      }

      if (allMatched.length > 0) {
        // Auto-import all matched children with default dates.
        // ProfileScreen will show the intro + dates confirmation afterward.
        setMatchedChildren(allMatched);

        // Notify about custom emails used (for reuse in other integrations)
        if (onEmailUsed) {
          if (useSameCredentials) {
            if (sharedEmail && !suggestedEmails.includes(sharedEmail)) {
              onEmailUsed(sharedEmail);
            }
          } else {
            Object.values(childCredentials).forEach(cred => {
              if (cred.email && !suggestedEmails.includes(cred.email)) {
                onEmailUsed(cred.email);
              }
            });
          }
        }

        // Auto-import for all matched children. The backend now uses the
        // child's existing active term planner — we no longer ask it to spawn a
        // half-term planner. Children with no active planner get collected so
        // the caller can route the user into the term planner creation flow.
        // Hold here until the import has actually fetched and cached each
        // child's clubs. The backend caches before it looks for a term planner,
        // so a NO_ACTIVE_TERM_PLANNER response still means the data landed —
        // that child just needs a planner creating next. Anything else is a
        // genuine failure and must not be walked past silently, or the user
        // reaches the term planner step with nothing to place.
        setStep(6); // Show importing spinner
        let lastTermPlannerData: { termPlannerUuid: string; childUuid: string } | undefined;
        const needsTermPlannerCreation: { childUuid: string; childName: string }[] = [];
        const importErrors: string[] = [];

        for (const child of allMatched) {
          try {
            console.log(`Auto-importing activities for ${child.cluubzChildName}...`);
            const response = await apiService.importSchoolsBuddyToTermPlanner(
              child.cluubzChildUuid,
              '',
              child.schoolsBuddyChildId,
              schoolStartTime,
              schoolEndTime,
              undefined
            );
            if (response.success && response.data?.termPlannerUuid) {
              lastTermPlannerData = { termPlannerUuid: response.data.termPlannerUuid, childUuid: child.cluubzChildUuid };
            } else if ((response as any).code === 'NO_ACTIVE_TERM_PLANNER' || (response as any).needsTermPlannerCreation) {
              // Clubs are cached; the child just has no planner yet.
              needsTermPlannerCreation.push({ childUuid: child.cluubzChildUuid, childName: child.cluubzChildName });
            } else {
              importErrors.push(`${child.cluubzChildName}: ${response.error || 'Import failed'}`);
            }
          } catch (err: any) {
            console.error(`Failed to auto-import for ${child.cluubzChildName}:`, err);
            importErrors.push(`${child.cluubzChildName}: ${err.message || 'Import failed'}`);
          }
        }

        if (importErrors.length > 0) {
          setError(
            'We connected to SchoolsBuddy but could not import the clubs:\n' +
            importErrors.join('\n') +
            '\n\nPlease try again — your term planner needs this data.'
          );
          setStep(2);
          return;
        }

        onClose(true, lastTermPlannerData, needsTermPlannerCreation.length ? needsTermPlannerCreation : undefined);
        return;
      } else if (connectionErrors.length > 0) {
        setError(connectionErrors.join('\n'));
        setStep(2);
      } else {
        setError('No children were matched with your SchoolsBuddy account.');
        setStep(2);
      }
    } catch (err: any) {
      console.error('SchoolsBuddy connection error:', err);
      setError(err.message || 'Failed to connect to SchoolsBuddy');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const handleImportForChild = async () => {
    if (!termName || !startDate || !endDate) {
      setError('Please fill in all term details');
      return;
    }

    const currentChild = matchedChildren[currentChildIndex];
    if (!currentChild) return;

    setLoading(true);
    setError(null);
    setStep(6); // Importing step

    try {
      console.log(`Importing activities for ${currentChild.cluubzChildName}...`);
      console.log(`🏫 School hours being sent: Start=${schoolStartTime}, End=${schoolEndTime}`);

      const response = await apiService.importSchoolsBuddyToTermPlanner(
        currentChild.cluubzChildUuid,
        '', // Backend will resolve to the child's existing active term planner.
        currentChild.schoolsBuddyChildId,
        schoolStartTime,
        schoolEndTime,
        undefined
      );

      if (response.success) {
        console.log(`✅ Imported activities for ${currentChild.cluubzChildName}`);
        const termPlannerUuid = response.data?.termPlannerUuid;

        // Move to next child or complete
        if (currentChildIndex < matchedChildren.length - 1) {
          setCurrentChildIndex(currentChildIndex + 1);
          // Reset term fields for next child (keep same defaults)
          setStep(5); // Term config step
        } else {
          onClose(true, termPlannerUuid ? { termPlannerUuid, childUuid: currentChild.cluubzChildUuid } : undefined);
        }
      } else if ((response as any).code === 'NO_ACTIVE_TERM_PLANNER' || (response as any).needsTermPlannerCreation) {
        // Hand off to the parent screen so it can open the term planner
        // creation flow for this child, then the user can re-trigger import.
        onClose(true, undefined, [{ childUuid: currentChild.cluubzChildUuid, childName: currentChild.cluubzChildName }]);
      } else {
        setError(response.error || 'Failed to import activities');
        setStep(5); // Back to term config on error
      }
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import activities');
      setStep(5); // Back to term config on error
    } finally {
      setLoading(false);
    }
  };

  const handleSkipChild = () => {
    // Skip this child and move to next
    if (currentChildIndex < matchedChildren.length - 1) {
      setCurrentChildIndex(currentChildIndex + 1);
    } else {
      // All children done (or skipped) - no term planner to open
      onClose(true, undefined);
    }
  };

  if (!isOpen) return null;

  const currentMatchedChild = matchedChildren[currentChildIndex];

  return (
    <div className="socs-modal-overlay">
      <div className="socs-modal">
        <div className="socs-modal-header" style={{
          background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
          borderRadius: '8px 8px 0 0'
        }}>
          <h2 style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>📚 Connect SchoolsBuddy</h2>
          <button className="close-btn" onClick={() => onClose(false)} style={{ color: 'white', opacity: 0.8 }}>×</button>
        </div>

        <div className="socs-modal-content">
          {error && (
            <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
              {error}
            </div>
          )}

          {/* Step 1: School Search */}
          {step === 1 && (
            <>
              {/* Only show instructions if no children have search results or selected sites */}
              {!children.some(c => (searchResults[c.uuid]?.length > 0) || selectedSite[c.uuid]) && (
                <p style={{ marginBottom: '16px' }}>
                  We'll search for your school's SchoolsBuddy site. You can set up each child individually.
                </p>
              )}

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {children.filter(c => !c.is_parent).map((child) => (
                  <div
                    key={child.uuid}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      backgroundColor: selectedSite[child.uuid] ? '#f0fff4' : 'white'
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                      {child.display_name || `${child.first_name} ${child.last_name}`}
                      {selectedSite[child.uuid] && (
                        <span style={{ marginLeft: '8px', color: '#28a745', fontSize: '14px' }}>✓ School Found</span>
                      )}
                    </div>

                    {/* Show searching spinner */}
                    {searching[child.uuid] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #e0e0e0',
                          borderTop: '2px solid #1976d2',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        <span style={{ fontSize: '13px', color: '#666' }}>
                          Searching for "{childSchoolNames[child.uuid]}"...
                        </span>
                      </div>
                    )}

                    {/* Show found school (collapsed) */}
                    {selectedSite[child.uuid] && !showSearchExpanded[child.uuid] && !searching[child.uuid] && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#666' }}>{selectedSite[child.uuid]}</span>
                        <button
                          type="button"
                          onClick={() => setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: true }))}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: 'transparent',
                            color: '#666',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Change
                        </button>
                      </div>
                    )}

                    {/* Show search field (expanded or no results) */}
                    {!searching[child.uuid] && (!selectedSite[child.uuid] || showSearchExpanded[child.uuid]) && (
                      <>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={childSchoolNames[child.uuid] || ''}
                            onChange={(e) => setChildSchoolNames(prev => ({ ...prev, [child.uuid]: e.target.value }))}
                            placeholder="School name"
                            className="form-input"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            onClick={() => handleSearchSchool(child.uuid)}
                            disabled={!childSchoolNames[child.uuid]?.trim()}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: childSchoolNames[child.uuid]?.trim() ? '#007bff' : '#ccc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: childSchoolNames[child.uuid]?.trim() ? 'pointer' : 'not-allowed',
                              fontSize: '13px'
                            }}
                          >
                            🔍 Search
                          </button>
                        </div>

                        {/* Show search results */}
                        {searchResults[child.uuid] && searchResults[child.uuid].length > 0 && (
                          <div style={{
                            marginBottom: '8px',
                            maxHeight: '150px',
                            overflowY: 'auto',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px'
                          }}>
                            {searchResults[child.uuid].map((site: any, index: number) => (
                              <div
                                key={index}
                                onClick={() => handleSelectSite(child.uuid, site.url)}
                                style={{
                                  padding: '10px',
                                  borderBottom: index < searchResults[child.uuid].length - 1 ? '1px solid #eee' : 'none',
                                  backgroundColor: selectedSite[child.uuid] === site.url ? '#d4edda' : 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <span style={{ fontSize: '13px' }}>{site.url}</span>
                                {selectedSite[child.uuid] === site.url && <span style={{ color: '#28a745' }}>✓</span>}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Manual URL input if no results */}
                        {searchResults[child.uuid]?.length === 0 && autoSearchTriggered[child.uuid] && (
                          <div style={{ marginTop: '8px' }}>
                            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                              No site found. Enter URL manually:
                            </p>
                            <input
                              type="text"
                              value={manualUrls[child.uuid] || ''}
                              onChange={(e) => {
                                setManualUrls(prev => ({ ...prev, [child.uuid]: e.target.value }));
                                setSelectedSite(prev => ({ ...prev, [child.uuid]: e.target.value }));
                              }}
                              placeholder="https://yourschool.schoolsbuddy.net"
                              className="form-input"
                              style={{ width: '100%' }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {getMatchedChildrenCount()} of {children.length} children have a school selected
                </p>
              </div>

              <button
                onClick={handleContinueToCredentials}
                disabled={getMatchedChildrenCount() === 0}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  marginTop: '16px',
                  backgroundColor: getMatchedChildrenCount() > 0 ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: getMatchedChildrenCount() > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}
              >
                Continue with {getMatchedChildrenCount()} {getMatchedChildrenCount() === 1 ? 'child' : 'children'} →
              </button>

              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </>
          )}

          {/* Step 2: Credentials */}
          {step === 2 && (
            <>
              <p style={{ marginBottom: '16px' }}>
                Enter your SchoolsBuddy login credentials for each school.
              </p>

              {/* Toggle for same credentials */}
              {getChildrenWithSchools().length > 1 && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useSameCredentials}
                      onChange={(e) => setUseSameCredentials(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      Use the same credentials for all children
                    </span>
                  </label>
                </div>
              )}

              {/* Shared credentials (when useSameCredentials is true) */}
              {useSameCredentials && (
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: 'white'
                }}>
                  <div style={{
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8'
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>
                      All Children
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {getChildrenWithSchools().map(c => c.first_name).join(', ')}
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Email</label>
                    {suggestedEmails.length > 0 && !showCustomEmailInput ? (
                      <select
                        value={sharedEmail}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            setShowCustomEmailInput(true);
                            setSharedEmail('');
                          } else {
                            setSharedEmail(e.target.value);
                          }
                        }}
                        className="form-input"
                        disabled={loading}
                        style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white' }}>
                        <option value="">Select an email...</option>
                        {suggestedEmails.map((email, idx) => (
                          <option key={idx} value={email}>{email}</option>
                        ))}
                        <option value="__custom__">+ Add different email</option>
                      </select>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="email"
                          value={sharedEmail}
                          onChange={(e) => setSharedEmail(e.target.value)}
                          className="form-input"
                          placeholder="your.email@example.com"
                          disabled={loading}
                          style={{ flex: 1 }}
                        />
                        {suggestedEmails.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowCustomEmailInput(false)}
                            style={{ padding: '10px 12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer', fontSize: '12px' }}>
                            ← Back
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showSharedPassword ? 'text' : 'password'}
                        value={sharedPassword}
                        onChange={(e) => setSharedPassword(e.target.value)}
                        className="form-input"
                        placeholder="Your SchoolsBuddy password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowSharedPassword(!showSharedPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        {showSharedPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    <a href="https://www.schoolsbuddy.com/Account/Login" target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '13px', color: '#1976d2', textDecoration: 'none', display: 'inline-block', marginTop: '6px' }}>
                      Forgot password? →
                    </a>
                  </div>
                </div>
              )}

              {/* Per-child credentials (when useSameCredentials is false) */}
              {!useSameCredentials && (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {getChildrenWithSchools().map((child) => (
                    <div
                      key={child.uuid}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        backgroundColor: 'white'
                      }}
                    >
                      <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '6px',
                        border: '1px solid #e8e8e8'
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>
                          {child.first_name} {child.last_name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          📚 {getSchoolNameFromUrl(getSchoolUrlForChild(child.uuid))}
                        </div>
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Email</label>
                        {suggestedEmails.length > 0 && !showCustomEmailInputPerChild[child.uuid] ? (
                          <select
                            value={childCredentials[child.uuid]?.email || ''}
                            onChange={(e) => {
                              if (e.target.value === '__custom__') {
                                setShowCustomEmailInputPerChild(prev => ({ ...prev, [child.uuid]: true }));
                                setChildCredentials(prev => ({
                                  ...prev,
                                  [child.uuid]: { ...prev[child.uuid], email: '', password: prev[child.uuid]?.password || '' }
                                }));
                              } else {
                                setChildCredentials(prev => ({
                                  ...prev,
                                  [child.uuid]: { ...prev[child.uuid], email: e.target.value, password: prev[child.uuid]?.password || '' }
                                }));
                              }
                            }}
                            className="form-input"
                            disabled={loading}
                            style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white' }}>
                            <option value="">Select an email...</option>
                            {suggestedEmails.map((email, idx) => (
                              <option key={idx} value={email}>{email}</option>
                            ))}
                            <option value="__custom__">+ Add different email</option>
                          </select>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="email"
                              value={childCredentials[child.uuid]?.email || ''}
                              onChange={(e) => setChildCredentials(prev => ({
                                ...prev,
                                [child.uuid]: { ...prev[child.uuid], email: e.target.value, password: prev[child.uuid]?.password || '' }
                              }))}
                              className="form-input"
                              placeholder="your.email@example.com"
                              disabled={loading}
                              style={{ flex: 1 }}
                            />
                            {suggestedEmails.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setShowCustomEmailInputPerChild(prev => ({ ...prev, [child.uuid]: false }))}
                                style={{ padding: '10px 12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer', fontSize: '12px' }}>
                                ← Back
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Password</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showPassword[child.uuid] ? 'text' : 'password'}
                            value={childCredentials[child.uuid]?.password || ''}
                            onChange={(e) => setChildCredentials(prev => ({
                              ...prev,
                              [child.uuid]: { ...prev[child.uuid], email: prev[child.uuid]?.email || '', password: e.target.value }
                            }))}
                            className="form-input"
                            placeholder="Your SchoolsBuddy password"
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, [child.uuid]: !prev[child.uuid] }))}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px'
                            }}
                          >
                            {showPassword[child.uuid] ? '👁️' : '👁️‍🗨️'}
                          </button>
                        </div>
                        <a href="https://www.schoolsbuddy.com/Account/Login" target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '13px', color: '#1976d2', textDecoration: 'none', display: 'inline-block', marginTop: '6px' }}>
                          Forgot password? →
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleConnect}
                disabled={loading || !areCredentialsComplete()}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: (!loading && areCredentialsComplete()) ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!loading && areCredentialsComplete()) ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}
              >
                {loading ? '⏳ Connecting...' : '🔗 Connect SchoolsBuddy'}
              </button>

              <button
                onClick={() => setStep(1)}
                style={{
                  width: '100%',
                  padding: '10px 24px',
                  marginTop: '8px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ← Back to School Selection
              </button>

              <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '12px' }}>
                🔒 Your credentials are encrypted and stored securely
              </p>
            </>
          )}

          {/* Step 3: Connecting */}
          {step === 3 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e0e0e0',
                borderTop: '4px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <p style={{ fontSize: '16px', fontWeight: 500 }}>
                Connecting to SchoolsBuddy...
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Linking account and matching children
              </p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* Step 5: Term Configuration per child */}
          {step === 5 && currentMatchedChild && (
            <>
              <div style={{
                padding: '12px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Setting up child {currentChildIndex + 1} of {matchedChildren.length}
                </p>
              </div>

              <div style={{
                border: '2px solid #28a745',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: '#f0fff4'
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
                  {currentMatchedChild.cluubzChildName}
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  Matched with SchoolsBuddy: {currentMatchedChild.schoolsBuddyName}
                </p>
              </div>

              <h4 style={{ marginBottom: '12px' }}>Term Details</h4>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label>Term Name</label>
                <select
                  value={termName}
                  onChange={(e) => setTermName(e.target.value)}
                  className="form-input"
                  style={{ width: '100%', padding: '10px' }}
                >
                  <option value="">Select a term...</option>
                  <option value="Autumn Half 1">Autumn Half 1</option>
                  <option value="Autumn Half 2">Autumn Half 2</option>
                  <option value="Spring Half 1">Spring Half 1</option>
                  <option value="Spring Half 2">Spring Half 2</option>
                  <option value="Summer Half 1">Summer Half 1</option>
                  <option value="Summer Half 2">Summer Half 2</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: '500', marginBottom: '6px', display: 'block' }}>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-input"
                    style={{ width: '100%', padding: '12px', boxSizing: 'border-box', maxWidth: '100%', WebkitAppearance: 'none' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: '500', marginBottom: '6px', display: 'block' }}>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-input"
                    style={{ width: '100%', padding: '12px', boxSizing: 'border-box', maxWidth: '100%', WebkitAppearance: 'none' }}
                  />
                </div>
              </div>

              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '8px', fontSize: '15px' }}>🏫 School Hours</h4>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                  Default times on days without clubs.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '12px' }}>Start</label>
                    <TimePickerDropdown
                      value={schoolStartTime}
                      onChange={setSchoolStartTime}
                      defaultScrollPosition="start"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '12px' }}>End</label>
                    <TimePickerDropdown
                      value={schoolEndTime}
                      onChange={setSchoolEndTime}
                      defaultScrollPosition="end"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleImportForChild}
                disabled={loading || !termName || !startDate || !endDate}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: (!loading && termName && startDate && endDate) ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!loading && termName && startDate && endDate) ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  marginBottom: '8px'
                }}
              >
                📥 Import Activities for {currentMatchedChild.cluubzChildName}
              </button>

              <button
                onClick={handleSkipChild}
                style={{
                  width: '100%',
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Skip this child
              </button>
            </>
          )}

          {/* Step 6: Importing */}
          {step === 6 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e0e0e0',
                borderTop: '4px solid #28a745',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <p style={{ fontSize: '16px', fontWeight: 500 }}>
                Import in progress...
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Fetching {currentMatchedChild?.cluubzChildName}'s clubs from SchoolsBuddy.
                Please stay on this step — your term planner is built from this data.
              </p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolsBuddySetupModal;
